import { Component, OnInit } from '@angular/core';
import * as Highcharts from 'highcharts';
import windbarb from 'highcharts/modules/windbarb';
import { ActivatedRoute, Router } from '@angular/router';
import { DataService, SmartIrrigationService } from 'src/api/services';
import { DataAnalysisTimeSeriesValue, SmartIrrigationRootNodeDto, SmartIrrigationWeatherStation } from 'src/api/models';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

windbarb(Highcharts);

interface WeatherMetric {
  label: string;
  value: string | number;
  unit: string;
  status: 'red' | 'amber' | 'green';
  statusLabel: string;
}

@Component({
  selector: 'app-smart-irrigation-weather-trend',
  templateUrl: './weather-trend.component.html',
  styleUrls: ['./weather-trend.component.scss']
})
export class SmartIrrigationWeatherTrendComponent implements OnInit {
  Highcharts = Highcharts;
  chartConstructor = 'chart';

  nodeId: number;
  node: SmartIrrigationRootNodeDto;
  weatherStation: SmartIrrigationWeatherStation;
  stationName = 'Weather Station';
  loading = true;
  noData = false;
  lastRefresh: Date | null = null;
  reportPeriod = '';

  weatherMetrics: WeatherMetric[] = [];
  windDirectionDegrees: number | null = null;
  temperatureChartOptions: Highcharts.Options | null = null;
  humidityChartOptions: Highcharts.Options | null = null;
  rainfallChartOptions: Highcharts.Options | null = null;
  hourlyRainfallChartOptions: Highcharts.Options | null = null;
  windSpeedChartOptions: Highcharts.Options | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private app: ApplicationConfigService,
    private irrigation: SmartIrrigationService,
    private dataService: DataService
  ) {}

  ngOnInit(): void {
    this.nodeId = +this.route.snapshot.paramMap.get('nodeId')!;
    const from = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000);
    this.reportPeriod = `${from.toLocaleDateString()} - ${new Date().toLocaleDateString()}`;
    this.load();
  }

  load(): void {
    this.loading = true;
    this.noData = false;

    forkJoin({
      dashboard: this.irrigation.SmartIrrigationGetDashboard(this.app.customerId),
      node: this.irrigation.SmartIrrigationGetNode({ customerId: this.app.customerId, nodeId: this.nodeId })
    }).subscribe(({ dashboard, node }) => {
      this.node = node;

      this.weatherStation = this.findWeatherStation(node, dashboard.weatherStations || []);
      if (!this.weatherStation) {
        this.loading = false;
        this.noData = true;
        return;
      }
      
      this.stationName = this.weatherStation.description || this.weatherStation.name || this.weatherStation.deviceEui;
      this.weatherMetrics = this.buildWeatherMetrics(this.weatherStation);
      this.windDirectionDegrees = this.weatherStation.currentWindDirection?.value ?? null;
      this.loadWeatherSeries(this.weatherStation);
    }, () => {
      this.loading = false;
      this.noData = true;
    });
  }

  private loadWeatherSeries(weatherStation: SmartIrrigationWeatherStation): void {
    forkJoin({
      temperature: this.fetchSeries(weatherStation.temperatureDataUri),
      humidity: this.fetchSeries(weatherStation.humidityDataUri),
      hourlyRainfall: this.fetchHourlyRainfall(weatherStation.rainfallDataUri),
      windDirection: this.fetchHourlySeries(weatherStation.windDirectionDataUri),
      windSpeed: this.fetchHourlySeries(weatherStation.windSpeedDataUri)
    }).subscribe(({ temperature, humidity, hourlyRainfall, windDirection, windSpeed }) => {
      this.temperatureChartOptions = temperature.length > 0
        ? this.buildTrendChart(temperature, 'Temperature', 'degrees C', '#f59f00')
        : null;
      this.humidityChartOptions = humidity.length > 0
        ? this.buildTrendChart(humidity, 'Humidity', '%', '#4299e1', 0, 100)
        : null;
      this.hourlyRainfallChartOptions = hourlyRainfall.length > 0
        ? this.buildHourlyRainfallChart(hourlyRainfall)
        : null;
      this.windSpeedChartOptions = windSpeed.length > 0
        ? this.buildTrendChart(windSpeed, 'Wind Speed', 'm/s', '#a855f7', undefined, undefined, windDirection)
        : null;
      this.loading = false;
      this.lastRefresh = new Date();
    }, () => {
      this.loading = false;
      this.noData = true;
    });
  }

  private findWeatherStation(
    node: SmartIrrigationRootNodeDto,
    weatherStations: SmartIrrigationWeatherStation[]
  ): SmartIrrigationWeatherStation | undefined {
    const weatherDeviceUris = (node.associatedDevices || [])
      .filter(device => (device.deviceTypeName || '').toLowerCase().includes('weather'))
      .map(device => this.normalize(device.uri))
      .filter(uri => !!uri);

    return weatherStations.find(station => {
      const stationIdentifiers = [station.deviceEui, station.siteUri]
        .map(identifier => this.normalize(identifier))
        .filter(identifier => !!identifier);
      return weatherDeviceUris.some(uri => stationIdentifiers.includes(uri));
    });
  }

  private normalize(value?: string): string {
    return (value || '').trim().toLowerCase();
  }

  private fetchSeries(uri?: string): Observable<DataAnalysisTimeSeriesValue[]> {
    if (!uri) {
      return of([]);
    }

    return this.dataService.DataGetData({
      customerId: this.app.customerId,
      request: {
        forceEmbed: true,
        index: 0,
        period: '-14d:1d',
        type: 'TimeSeries',
        timeZoneOffset: this.app.getTimeZoneOffset(null),
        timeSeries: { single: { variable: { uri } } }
      }
    }).pipe(map(result => result?.values || []));
  }

  private fetchHourlyRainfall(uri?: string): Observable<DataAnalysisTimeSeriesValue[]> {
    if (!uri) {
      return of([]);
    }

    return this.dataService.DataGetData({
      customerId: this.app.customerId,
      request: {
        forceEmbed: true,
        index: 0,
        period: '-14d:1h',
        type: 'TimeSeries',
        timeZoneOffset: this.app.getTimeZoneOffset(null),
        timeSeries: {
          single: {
            variable: {
              uri,
              transforms: [{
                transform: { id: 'aggregate', name: 'Aggregate' },
                config: [
                  { name: 'algorithm', value: 'Change' },
                  { name: 'period', value: 'hour' },
                  { name: 'tzConvert', value: 'false' }
                ]
              }]
            }
          }
        }
      }
    }).pipe(map(result => result?.values || []));
  }

  private fetchHourlySeries(uri?: string): Observable<DataAnalysisTimeSeriesValue[]> {
    if (!uri) {
      return of([]);
    }

    return this.dataService.DataGetData({
      customerId: this.app.customerId,
      request: {
        forceEmbed: true,
        index: 0,
        period: '-14d:1h',
        type: 'TimeSeries',
        timeZoneOffset: this.app.getTimeZoneOffset(null),
        timeSeries: { single: { variable: { uri } } }
      }
    }).pipe(map(result => result?.values || []));
  }

  private buildTrendChart(
    values: DataAnalysisTimeSeriesValue[],
    name: string,
    unit: string,
    color: string,
    min?: number,
    max?: number,
    windDirectionValues: DataAnalysisTimeSeriesValue[] = []
  ): Highcharts.Options {
    const data: [number, number | null][] = [...values]
      .sort((a, b) => new Date(a.timeStamp).getTime() - new Date(b.timeStamp).getTime())
      .map(value => [new Date(value.timeStamp).getTime(), value.value ?? null]);
    const now = Date.now();
    const maxX = this.getMidnightTonightMs();

    return {
      chart: { type: 'area', backgroundColor: 'transparent', style: { fontFamily: 'inherit' }, spacing: [16, 16, 16, 16] },
      title: { text: undefined },
      xAxis: {
        type: 'datetime',
        min: maxX - 14 * 24 * 3600 * 1000,
        max: maxX,
        tickInterval: 24 * 3600 * 1000,
        gridLineWidth: 1,
        plotLines: [{ value: now, color: 'rgba(255,255,255,0.45)', width: 1, dashStyle: 'Dash', zIndex: 4 }],
        labels: { format: '{value:%d %b}', rotation: -30 }
      },
      yAxis: { min, max, title: { text: unit } },
      tooltip: { shared: true, valueSuffix: ` ${unit}`, xDateFormat: '%d %b %Y %H:%M' },
      legend: { enabled: false },
      credits: { enabled: false },
      plotOptions: { area: { lineWidth: 2, marker: { enabled: false, radius: 3 }, fillOpacity: 0.15 } },
      series: [{
        name,
        type: 'area',
        data,
        color,
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [[0, Highcharts.color(color).setOpacity(0.3).get('rgba') as string], [1, Highcharts.color(color).setOpacity(0).get('rgba') as string]] as any
        }
      } as Highcharts.SeriesAreaOptions,
        ...this.buildWindbarbSeries(data, windDirectionValues)
      ]
    };
  }

  private buildWindbarbSeries(
    windSpeedData: [number, number | null][],
    windDirectionValues: DataAnalysisTimeSeriesValue[]
  ): Highcharts.SeriesOptionsType[] {
    if (windDirectionValues.length === 0) {
      return [];
    }

    const directions = new Map<number, number>();
    windDirectionValues.forEach(value => {
      if (value.value != null) {
        directions.set(new Date(value.timeStamp).getTime(), value.value);
      }
    });

    const samples = windSpeedData
      .filter(([timestamp, speed]) => speed != null && directions.has(timestamp))
      .map(([timestamp, speed]): [number, number, number] => [timestamp, speed as number, directions.get(timestamp)!]);
    const fourHoursMs = 4 * 60 * 60 * 1000;
    const buckets = new Map<number, Array<[number, number, number]>>();

    samples.forEach(sample => {
      const bucketStart = Math.floor(sample[0] / fourHoursMs) * fourHoursMs;
      const bucket = buckets.get(bucketStart) || [];
      bucket.push(sample);
      buckets.set(bucketStart, bucket);
    });

    const data = Array.from(buckets.entries())
      .sort(([firstBucket], [secondBucket]) => firstBucket - secondBucket)
      .map(([bucketStart, bucket]) => {
        const averageSpeed = bucket.reduce((total, [, speed]) => total + speed, 0) / bucket.length;
        const directionComponents = bucket.reduce((components, [, , direction]) => {
          const radians = direction * Math.PI / 180;
          return {
            northSouth: components.northSouth + Math.cos(radians),
            eastWest: components.eastWest + Math.sin(radians)
          };
        }, { northSouth: 0, eastWest: 0 });
        const averageDirection = (Math.atan2(directionComponents.eastWest, directionComponents.northSouth) * 180 / Math.PI + 360) % 360;
        return [bucketStart + fourHoursMs / 2, averageSpeed, averageDirection];
      });

    return data.length > 0
      ? [{
          name: 'Wind Direction',
          type: 'windbarb',
          data,
          color: '#e2e8f0',
          lineWidth: 1,
          vectorLength: 18,
          yOffset: -18,
          tooltip: { valueSuffix: ' m/s' }
        } as any]
      : [];
  }

  private getMidnightTonightMs(): number {
    const tonight = new Date();
    tonight.setHours(24, 0, 0, 0);
    return tonight.getTime();
  }

  private buildHourlyRainfallChart(values: DataAnalysisTimeSeriesValue[]): Highcharts.Options {
    const data: [number, number][] = [...values]
      .filter(value => value.value != null)
      .sort((a, b) => new Date(a.timeStamp).getTime() - new Date(b.timeStamp).getTime())
      .map(value => [new Date(value.timeStamp).getTime(), value.value]);
    const now = Date.now();
    const maxX = this.getMidnightTonightMs();

    return {
      chart: { type: 'column', backgroundColor: 'transparent', style: { fontFamily: 'inherit' }, spacing: [16, 16, 16, 16] },
      title: { text: undefined },
      xAxis: {
        type: 'datetime',
        min: maxX - 14 * 24 * 3600 * 1000,
        max: maxX,
        tickInterval: 24 * 3600 * 1000,
        gridLineWidth: 1,
        plotLines: [{ value: now, color: 'rgba(255,255,255,0.45)', width: 1, dashStyle: 'Dash', zIndex: 4 }],
        labels: { format: '{value:%d %b}', rotation: -30 }
      },
      yAxis: { min: 0, title: { text: 'mm' } },
      tooltip: { valueSuffix: ' mm', xDateFormat: '%d %b %Y %H:%M' },
      legend: { enabled: false },
      credits: { enabled: false },
      plotOptions: { column: { borderWidth: 0, pointPadding: 0.05, groupPadding: 0 } },
      series: [{ name: 'Hourly Rainfall', type: 'column', data, color: '#20c997' } as Highcharts.SeriesColumnOptions]
    };
  }

  private buildWeatherMetrics(weatherStation: SmartIrrigationWeatherStation): WeatherMetric[] {
    return [
      this.windMetric(weatherStation.currentWindSpeed?.value),
      this.rainfallMetric(weatherStation.currentRainfall?.value),
      this.temperatureMetric(weatherStation.currentTemperature?.value),
      this.humidityMetric(weatherStation.currentHumidity?.value)
    ];
  }

  private windMetric(value: number): WeatherMetric {
    const status: WeatherMetric['status'] = value == null ? 'green' : value > 17 ? 'red' : value > 11 ? 'amber' : 'green';
    return { label: 'Wind Speed', value: value != null ? value.toFixed(1) : 'N/A', unit: 'm/s', status, statusLabel: status === 'red' ? 'High' : status === 'amber' ? 'Elevated' : 'Normal' };
  }

  private rainfallMetric(value: number): WeatherMetric {
    const status: WeatherMetric['status'] = value == null ? 'green' : value > 50 ? 'red' : value > 20 ? 'amber' : 'green';
    return { label: 'Rainfall (24h)', value: value != null ? value.toFixed(1) : 'N/A', unit: 'mm', status, statusLabel: status === 'red' ? 'Heavy' : status === 'amber' ? 'Elevated' : 'Normal' };
  }

  private temperatureMetric(value: number): WeatherMetric {
    return { label: 'Temperature', value: value != null ? value.toFixed(1) : 'N/A', unit: 'degrees C', status: 'green', statusLabel: 'Measured' };
  }

  private humidityMetric(value: number): WeatherMetric {
    return { label: 'Humidity', value: value != null ? value.toFixed(0) : 'N/A', unit: '%', status: 'green', statusLabel: 'Measured' };
  }

  goBack(): void {
    this.router.navigate(['/smart-irrigation/site', this.nodeId]);
  }
}
